import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import {
	getAdminProduct,
	listAdminProducts,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { useDebounce } from "@/lib/use-debounce";
import {
	EMPTY_RULE_FORM,
	QUANTITY_KINDS,
	type RuleForm,
	ruleFormFrom,
	rulesFromForm,
	UNITS,
} from "../lib/rules";
import {
	createItem,
	type TemplateItem,
	updateItem,
} from "../services/boq-templates-api.service";

/** `SKU`, plus its axes when it has them: "TL-60 · 600x600 · Matt". */
function variantLabel(v: {
	sku: string;
	variantAxis1Value: string | null;
	variantAxis2Value: string | null;
}): string {
	return [v.sku, v.variantAxis1Value, v.variantAxis2Value]
		.filter(Boolean)
		.join(" · ");
}

const TITLE_ID = "boq-template-item-title";
const selectClass =
	"h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm";

/** Add a line to a section, or edit one (`item` set). */
export function ItemDialog({
	sectionId,
	item,
	onClose,
	onSaved,
}: {
	sectionId: string;
	item?: TemplateItem;
	onClose: () => void;
	onSaved: () => void;
}) {
	const [product, setProduct] = useState<{ id: string; name: string } | null>(
		item ? { id: item.catalogProductId, name: item.productName } : null,
	);
	const [search, setSearch] = useState("");
	const debounced = useDebounce(search, 300);
	const [variantId, setVariantId] = useState(item?.catalogVariantId ?? "");
	const [label, setLabel] = useState(item?.labelOverride ?? "");
	const [unit, setUnit] = useState(item?.defaultUnit ?? "");
	const [optional, setOptional] = useState(item?.isOptional ?? false);
	const [rules, setRules] = useState<RuleForm>(
		item ? ruleFormFrom(item.quantityRule, item.priceRule) : EMPTY_RULE_FORM,
	);
	const [error, setError] = useState<string | null>(null);

	const results = useQuery({
		queryKey: ["admin-boq-template-product-search", debounced],
		queryFn: () => listAdminProducts({ q: debounced.trim(), perPage: 8 }),
		enabled: debounced.trim().length >= 2,
	});

	// Variants come from the product itself, so the author picks from what the
	// product has rather than typing an id. Retired variants are never returned.
	const variants = useQuery({
		queryKey: ["admin-boq-template-product-variants", product?.id],
		queryFn: async () =>
			(await getAdminProduct(product?.id ?? "")).variants.filter(
				(v) => v.isActive,
			),
		enabled: Boolean(product),
	});

	const set = <K extends keyof RuleForm>(key: K, value: RuleForm[K]) =>
		setRules((r) => ({ ...r, [key]: value }));

	const save = useMutation({
		mutationFn: async () => {
			if (!product) throw new Error("Pick a catalogue product");
			const { quantityRule, priceRule } = rulesFromForm(rules);
			const body = {
				catalogProductId: product.id,
				catalogVariantId: variantId || null,
				labelOverride: label.trim() || null,
				defaultUnit: unit || null,
				isOptional: optional,
				quantityRule,
				priceRule,
			};
			if (item) await updateItem(item.id, body);
			else await createItem(sectionId, body);
		},
		onSuccess: () => {
			onSaved();
			onClose();
		},
	});

	const needsMultiplier = rules.quantityKind !== "fixed";

	return (
		<Dialog open titleId={TITLE_ID} onClose={onClose}>
			<h3 id={TITLE_ID} className="text-lg font-semibold">
				{item ? "Edit line" : "Add a line"}
			</h3>

			<div className="grid gap-1">
				<Label htmlFor="item-product-search">Catalogue product</Label>
				{product ? (
					<div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
						<span>{product.name}</span>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={() => setProduct(null)}
						>
							Change
						</Button>
					</div>
				) : (
					<>
						<Input
							id="item-product-search"
							placeholder="Search by name or SKU (2+ characters)"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<ul className="grid max-h-40 gap-1 overflow-auto">
							{(results.data?.items ?? []).map((p) => (
								<li key={p.id}>
									<button
										type="button"
										className="w-full rounded-md border px-3 py-1.5 text-left text-sm hover:bg-slate-50"
										onClick={() => {
											setProduct({ id: p.id, name: p.name });
											// A variant belongs to one product, so a new product starts with none.
											setVariantId("");
										}}
									>
										{p.name}
										<span className="ml-2 text-xs text-muted-foreground">
											{p.sku ?? "no SKU"}
											{p.brandName ? ` · ${p.brandName}` : ""}
										</span>
									</button>
								</li>
							))}
						</ul>
					</>
				)}
			</div>

			{product && (
				<div className="grid gap-1">
					<Label htmlFor="item-variant">Variant</Label>
					<select
						id="item-variant"
						className={selectClass}
						value={variantId}
						onChange={(e) => setVariantId(e.target.value)}
					>
						<option value="">Any variant: use the product's own price</option>
						{item?.catalogVariantId &&
							item.catalogProductId === product.id &&
							!(variants.data ?? []).some(
								(v) => v.id === item.catalogVariantId,
							) && (
								<option value={item.catalogVariantId}>
									{item.variantSku ?? "Current variant"} (retired or off)
								</option>
							)}
						{(variants.data ?? []).map((v) => (
							<option key={v.id} value={v.id}>
								{variantLabel(v)}
							</option>
						))}
					</select>
					{variants.isError && (
						<p className="text-xs text-red-700">
							Could not load this product's variants.
						</p>
					)}
				</div>
			)}

			<div className="grid gap-1">
				<Label htmlFor="item-label">Label override (optional)</Label>
				<Input
					id="item-label"
					value={label}
					onChange={(e) => setLabel(e.target.value)}
				/>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="grid gap-1">
					<Label htmlFor="item-unit">Unit</Label>
					<select
						id="item-unit"
						className={selectClass}
						value={unit}
						onChange={(e) => setUnit(e.target.value)}
					>
						<option value="">Product default</option>
						{UNITS.map((u) => (
							<option key={u} value={u}>
								{u}
							</option>
						))}
					</select>
				</div>
				<label className="mt-6 flex items-center gap-2 text-sm">
					<input
						type="checkbox"
						checked={optional}
						onChange={(e) => setOptional(e.target.checked)}
					/>
					Optional for the homeowner
				</label>
			</div>

			<fieldset className="grid gap-2 rounded-md border p-3">
				<legend className="px-1 text-xs font-semibold uppercase">
					Quantity
				</legend>
				<select
					aria-label="Quantity rule"
					className={selectClass}
					value={rules.quantityKind}
					onChange={(e) =>
						set("quantityKind", e.target.value as RuleForm["quantityKind"])
					}
				>
					{QUANTITY_KINDS.map((k) => (
						<option key={k.value} value={k.value}>
							{k.label}
						</option>
					))}
				</select>
				{rules.quantityKind === "room_count" && (
					<select
						aria-label="Room source"
						className={selectClass}
						value={rules.roomSource}
						onChange={(e) =>
							set("roomSource", e.target.value as RuleForm["roomSource"])
						}
					>
						<option value="property.rooms.bedrooms">Bedrooms</option>
						<option value="property.rooms.bathrooms">Bathrooms</option>
					</select>
				)}
				{rules.quantityKind === "fixed" ? (
					<Input
						aria-label="Quantity"
						inputMode="decimal"
						value={rules.value}
						onChange={(e) => set("value", e.target.value)}
					/>
				) : null}
				{needsMultiplier && (
					<div className="grid grid-cols-2 gap-2">
						<Input
							aria-label="Multiplier"
							placeholder="Multiplier"
							inputMode="decimal"
							value={rules.multiplier}
							onChange={(e) => set("multiplier", e.target.value)}
						/>
						<Input
							aria-label="Fallback quantity"
							placeholder="Fallback quantity"
							inputMode="decimal"
							value={rules.fallbackQuantity}
							onChange={(e) => set("fallbackQuantity", e.target.value)}
						/>
					</div>
				)}
			</fieldset>

			<fieldset className="grid gap-2 rounded-md border p-3">
				<legend className="px-1 text-xs font-semibold uppercase">Price</legend>
				<select
					aria-label="Price rule"
					className={selectClass}
					value={rules.priceKind}
					onChange={(e) =>
						set("priceKind", e.target.value as RuleForm["priceKind"])
					}
				>
					<option value="catalog_price">Catalogue price</option>
					<option value="fixed">Fixed price</option>
				</select>
				{rules.priceKind === "fixed" && (
					<Input
						aria-label="Fixed price (₹)"
						placeholder="Amount in ₹"
						inputMode="decimal"
						value={rules.priceValue}
						onChange={(e) => set("priceValue", e.target.value)}
					/>
				)}
			</fieldset>

			{error && (
				<p role="alert" className="text-sm text-red-700">
					{error}
				</p>
			)}
			<div className="flex justify-end gap-2">
				<Button type="button" variant="outline" onClick={onClose}>
					Cancel
				</Button>
				<Button
					type="button"
					disabled={save.isPending}
					onClick={() => {
						setError(null);
						save.mutate(undefined, {
							onError: (e) =>
								setError(
									e instanceof Error && !("response" in e)
										? e.message
										: apiErrorMessage(e, "Could not save this line"),
								),
						});
					}}
				>
					{item ? "Save line" : "Add line"}
				</Button>
			</div>
		</Dialog>
	);
}
